import { useQuery } from "@tanstack/react-query";
import { useAppConfig } from "@/appConfig";
import { getTourSamples } from "@/services/onboardingService";
import { getLocalStorage } from "@/helpers/common";
import { USER_INFO_KEY } from "@/constants/common";

export const FALLBACK_SAMPLES = {
  market: { city: null, state: null, neighborhood: null, fromMarket: false },
  wantListing: {
    title: "Looking for a move-in ready 3-bed with a big yard and room to\u00a0grow",
    budgetMin: 300000,
    budgetMax: 350000,
    bedrooms: 3,
    bathrooms: 2,
    sqftMin: 1600,
    sqftMax: 2200,
    radiusMiles: 10,
    niceToHaves: [
      { Text: "Fenced Yard", Icon: "fenced-yard.svg", Img: "fenced-yard-img.jpg", Tab: "Exterior" },
      { Text: "Office", Icon: "office.svg", Img: "office-img.jpg", Tab: "Layout" },
      { Text: "Kitchen Island", Icon: "kitchen-island.svg", Img: "kitchen-island-img.jpg", Tab: "Interior" },
      { Text: "Enclosed Porch/Patio", Icon: "covered-patio.svg", Img: "covered-patio-img.jpg", Tab: "Exterior" },
    ],
    matchingHomes: 12,
    bestMatch: 94,
  },
  property: {
    price: 339900,
    bedrooms: 3,
    bathrooms: 2,
    sqft: 1840,
    yearBuilt: 2004,
    neighborhood: null,
    demandCount: 7,
    demandBudgetMin: 315000,
    demandBudgetMax: 365000,
  },
  introduction: { price: 334900, bedrooms: 3, bathrooms: 2, sqft: 1910, neighborhood: null },
};

const roundTo = (value, step, direction = Math.round) => direction(value / step) * step;
const listPrice = (value) => roundTo(value, 5000) - 100;

// The tenant's hand-written buyer owns the whole sample. The API's property,
// introduction and match count were sized for a different budget, so they are
// re-derived from the tenant buyer (same rounding as the API) to keep the
// tour's numbers consistent. The tenant's market neighborhood (null when
// unset) wins too, so the sample property never names another area.
const deriveFromBuyer = (samples, wantListing, market) => {
  const { budgetMin, budgetMax, bedrooms, bathrooms, sqftMin, sqftMax } = wantListing;
  const price = listPrice((budgetMin + budgetMax) / 2);
  const sqft = roundTo((sqftMin + sqftMax) / 2, 10);
  return {
    wantListing: { ...wantListing, matchingHomes: FALLBACK_SAMPLES.wantListing.matchingHomes },
    property: {
      ...samples.property,
      price,
      bedrooms,
      bathrooms,
      sqft,
      neighborhood: market.neighborhood || null,
      demandBudgetMin: roundTo(price * 0.94, 5000, Math.floor),
      demandBudgetMax: roundTo(price * 1.06, 5000, Math.ceil),
    },
    introduction: {
      ...samples.introduction,
      price: listPrice(price * 0.98),
      bedrooms,
      bathrooms,
      sqft: roundTo(sqft * 1.04, 10),
      neighborhood: market.neighborhood || null,
    },
  };
};

export const withTenantSample = (samples, tenant) => {
  const custom = tenant?.tourSample;
  if (!custom) return samples;
  const market = { ...samples.market, ...custom.market };
  const wantListing = { ...samples.wantListing, ...custom.wantListing };
  const ownsBuyer = ["budgetMin", "budgetMax", "sqftMin", "sqftMax"].every((key) => Number.isFinite(custom.wantListing?.[key]));
  return {
    ...samples,
    market,
    ...(ownsBuyer ? deriveFromBuyer(samples, wantListing, market) : { wantListing }),
  };
};

export const iconOf = (icon) => {
  try {
    return require(`@/images/${icon}`);
  } catch {
    return require("@/images/lake-view-icon.svg");
  }
};

export const useTourSamples = (enabled = true) => {
  // The API sizes samples to the member's MLS, so a different user in the same
  // tab must not inherit the previous user's cached samples.
  const userId = getLocalStorage(USER_INFO_KEY)?.userInfo?.userId ?? null;
  const query = useQuery({
    queryKey: ["tourSamples", userId],
    queryFn: async () => {
      const response = await getTourSamples();
      if (response?.errorCode !== 0 || !response?.result) throw new Error("No tour samples");
      return response.result;
    },
    enabled,
    staleTime: Infinity,
    retry: 1,
  });
  const { tenant } = useAppConfig() || {};
  return withTenantSample(query.data || FALLBACK_SAMPLES, tenant);
};
